import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useSettings } from "@/context/SettingsContext";
import { useLanguage } from "@/context/LanguageContext";
import { CONSENT_I18N } from "@/i18n/consent";

/* GDPR-style cookie consent. Shown only when admin enables consent and the
 * visitor has not yet made a choice. Blocks analytics/marketing until granted. */
export const ConsentBanner = () => {
  const { settings, consentDecided, setConsent } = useSettings();
  const { locale } = useLanguage();
  const location = useLocation();
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);
  // Defer the banner until the browser is idle so its (large) text block is not
  // the Largest Contentful Paint — keeps the hero as the LCP element.
  const [ready, setReady] = useState(false);
  React.useEffect(() => {
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1800));
    const cic = window.cancelIdleCallback || clearTimeout;
    const id = ric(() => setReady(true), { timeout: 2500 });
    return () => cic(id);
  }, []);
  const c = CONSENT_I18N[locale] || CONSENT_I18N.en;

  const enabled = settings?.consent?.enabled !== false;
  const internal = /^\/(admin|dashboard|owner)/.test(location.pathname);
  if (!ready || !enabled || consentDecided || internal) return null;

  const acceptAll = () => setConsent({ analytics: true, marketing: true });
  const rejectAll = () => setConsent({ analytics: false, marketing: false });
  const savePrefs = () => setConsent({ analytics, marketing });

  return (
    <div
      data-testid="consent-banner"
      className="fixed bottom-4 left-1/2 z-[200] w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 rounded-[18px] border border-[color:var(--glass-border)] bg-white/90 p-5 shadow-[var(--shadow-glass)] backdrop-blur-xl sm:p-6"
    >
      <div className="flex items-start gap-4">
        <div className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color:var(--lux-ink)] text-[color:var(--lux-ivory)] sm:flex">
          <Cookie className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-[Gloock] text-lg text-[color:var(--lux-ink)]">{c.title}</h3>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {c.body}
          </p>

          {customize && (
            <div className="mt-4 space-y-3 rounded-[14px] border border-border bg-secondary/40 p-4">
              <div className="flex items-center justify-between opacity-70">
                <div>
                  <Label className="text-sm font-medium">{c.essential}</Label>
                  <p className="text-xs text-muted-foreground">{c.essentialDesc}</p>
                </div>
                <Switch checked disabled aria-readonly data-testid="consent-essential-switch" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="consent-analytics" className="text-sm font-medium">{c.analytics}</Label>
                  <p className="text-xs text-muted-foreground">{c.analyticsDesc}</p>
                </div>
                <Switch id="consent-analytics" checked={analytics} onCheckedChange={setAnalytics} data-testid="consent-analytics-switch" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="consent-marketing" className="text-sm font-medium">{c.marketing}</Label>
                  <p className="text-xs text-muted-foreground">{c.marketingDesc}</p>
                </div>
                <Switch id="consent-marketing" checked={marketing} onCheckedChange={setMarketing} data-testid="consent-marketing-switch" />
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            {!customize ? (
              <>
                <Button onClick={acceptAll} data-testid="consent-accept-all" className="h-10 bg-[color:var(--lux-ink)] text-[color:var(--lux-ivory)] hover:bg-black">
                  {c.acceptAll}
                </Button>
                <Button onClick={rejectAll} variant="outline" data-testid="consent-reject" className="h-10">
                  {c.reject}
                </Button>
                <Button onClick={() => setCustomize(true)} variant="ghost" data-testid="consent-customize-toggle" className="h-10">
                  {c.customise}
                </Button>
              </>
            ) : (
              <>
                <Button onClick={savePrefs} data-testid="consent-save" className="h-10 bg-[color:var(--lux-ink)] text-[color:var(--lux-ivory)] hover:bg-black">
                  {c.save}
                </Button>
                <Button onClick={acceptAll} variant="outline" data-testid="consent-accept-all-2" className="h-10">
                  {c.acceptAll}
                </Button>
              </>
            )}
          </div>
        </div>
        <button
          onClick={rejectAll}
          aria-label="Dismiss and reject non-essential cookies"
          data-testid="consent-dismiss"
          className="shrink-0 rounded-full p-1 text-muted-foreground transition-colors hover:bg-black/5 hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default ConsentBanner;
